import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Ban, Lock } from 'lucide-react';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import { Money, Empty, fmtDate, usd, pct, parseCents, centsToInput, today, thisYear, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

// Deal payouts under the First Amendment, sec 5.1: fees are calculated from cash received
// minus the deal's direct costs. Nothing is paid by the system; Chad records each payment
// by hand with a reference, and each record keeps the agreement version it was worked under.

const MEMBERS = ['Chad', 'Steve', 'Chase'];
const STATUS_LABEL = { draft: 'Draft', pending_signature: 'Awaiting signatures', signed: 'Signed', superseded: 'Superseded' };

export default function PayoutsTab({ me, deals: dealsShared, reloadShared, onGo }) {
  const [deals, setDeals] = useState(dealsShared);
  const [agreements, setAgreements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(thisYear());
  const [open, setOpen] = useState(null);
  const [editDeal, setEditDeal] = useState(null);
  const [editAg, setEditAg] = useState(null);
  const [error, setError] = useState('');
  const [paybacks, setPaybacks] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, a, s] = await Promise.all([api.get('/books/deals'), api.get('/books/agreements'), api.get(`/books/payouts/summary?year=${year}`)]);
      setDeals(d.deals); setAgreements(a.agreements); setSummary(s); setError('');
    } catch (e) { setError(e.message); }
    // Partner paybacks (LP-309 slice B): while any is open, the database refuses fee payouts.
    try { setPaybacks(await api.get('/books/paybacks/status')); } catch { setPaybacks(null); }
  }, [year]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); reloadShared(); };

  return (
    <div className="space-y-5">
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {paybacks?.fee_payouts_blocked && (
        <div role="status" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
          <Lock size={15} />
          <span>Fee payouts are blocked: CCS still owes {usd(paybacks.total_cents)} in partner paybacks. Repay them first (paybacks come before role fees); marking a fee paid is refused until then.</span>
          {onGo && <button className="underline" onClick={() => onGo('paybacks')}>Paybacks</button>}
        </div>
      )}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="font-semibold text-[var(--text-primary)]">Members, {year}</h2>
          <select className={inputCls} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">
            {[thisYear(), thisYear() - 1, thisYear() - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {summary && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {summary.members.map((m) => (
                <div key={m.member} className={cardCls}>
                  <div className="mb-2 font-semibold text-[var(--text-primary)]">{m.member}</div>
                  <dl className="grid grid-cols-2 gap-2 text-xs">
                    <div><dt className="text-[var(--text-secondary)]">Fees earned</dt><dd className="text-sm font-semibold"><Money cents={m.fees_earned_cents} /></dd></div>
                    <div><dt className="text-[var(--text-secondary)]">Fees paid</dt><dd className="text-sm font-semibold text-emerald-700 dark:text-emerald-400"><Money cents={m.fees_paid_cents} /></dd></div>
                    <div><dt className="text-[var(--text-secondary)]">Due now (all years)</dt><dd className="text-sm font-semibold text-amber-700 dark:text-amber-400"><Money cents={m.fees_due_now_cents} /></dd></div>
                    <div><dt className="text-[var(--text-secondary)]">Company share, by ownership</dt><dd className="text-sm"><Money cents={m.company_share_indicative_cents} /></dd></div>
                  </dl>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-[var(--text-secondary)]">
              The Company share is shown split 40/40/20 for reference only; distributions are decided by the members after overhead.
              {summary.control.difference_cents !== 0 && ` Fees marked paid (${usd(summary.control.payouts_marked_paid_cents)}) differ from the Member Fees account (${usd(summary.control.member_fees_account_cents)}); categorize fee payments to that account.`}
            </p>
          </>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">Deals</h2>
          {me.can_edit && <button className={btnCls} onClick={() => setEditDeal({})}><Plus size={14} /> New deal</button>}
        </div>
        {deals.length === 0 ? <Empty>No deals yet. Create one, then tag its deposits and direct costs to it when categorizing.</Empty> : (
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                <th className={thCls}>Deal</th><th className={thCls}>Agreement</th><th className={`${thCls} text-right`}>Received</th><th className={`${thCls} text-right`}>Direct costs</th>
                <th className={`${thCls} text-right`}>Net proceeds</th><th className={`${thCls} text-right`}>Fees due</th>
              </tr></thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id} className="cursor-pointer border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800" onClick={() => setOpen(d.id)}>
                    <td className={tdCls}><div className="font-medium text-[var(--text-primary)]">{d.name}</div><div className="text-xs text-[var(--text-secondary)]">{d.customer}{d.status === 'final' ? ' / final' : ''}{d.collected_bps != null ? ` / ${pct(d.collected_bps)} collected` : ''}</div></td>
                    <td className={`${tdCls} text-xs`}>{d.agreement?.version_key}</td>
                    <td className={`${tdCls} text-right`}><Money cents={d.receipts_cents} /></td>
                    <td className={`${tdCls} text-right`}><Money cents={d.costs_cents} /></td>
                    <td className={`${tdCls} text-right`}><Money cents={d.net_proceeds_cents} /></td>
                    <td className={`${tdCls} text-right font-medium`}><Money cents={d.total_due_cents} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">Agreement versions</h2>
          {me.can_edit && <button className={btn2Cls} onClick={() => setEditAg({})}><Plus size={14} /> New version</button>}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {agreements.map((a) => (
            <div key={a.id} className={cardCls}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-[var(--text-primary)]">{a.title}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{a.version_key} / {STATUS_LABEL[a.status]}{a.is_default ? ' / default for new deals' : ''}{a.effective_date ? ` / effective ${fmtDate(a.effective_date)}` : ''}</div>
                </div>
                {me.can_edit && <button className="text-[var(--text-secondary)]" onClick={() => setEditAg(a)} aria-label="Edit"><Pencil size={14} /></button>}
              </div>
              <ul className="mt-2 text-sm">
                {a.config.roles.map((r) => <li key={r.key}>{r.label}: {pct(r.bps)} of {r.of === 'net' ? 'net proceeds' : 'the Company share'}{r.if_unassigned !== 'company' ? `, if nobody holds it goes to ${r.if_unassigned.slice(5)}` : ', stays with the Company if nobody holds it'}</li>)}
                <li className="text-xs text-[var(--text-secondary)]">Ownership: {MEMBERS.map((m) => `${m} ${pct(a.config.ownership_bps[m])}`).join(', ')}</li>
              </ul>
              {a.notes && <p className="mt-1 text-xs text-[var(--text-secondary)]">{a.notes}</p>}
            </div>
          ))}
        </div>
      </section>

      {open && <DealSheet id={open} me={me} onClose={() => setOpen(null)} onEdit={(d) => { setOpen(null); setEditDeal(d); }} onChanged={refresh} />}
      {editDeal && <DealForm deal={editDeal.id ? editDeal : null} agreements={agreements} onClose={() => setEditDeal(null)} onSaved={() => { setEditDeal(null); refresh(); }} />}
      {editAg && <AgreementForm agreement={editAg.id ? editAg : null} onClose={() => setEditAg(null)} onSaved={() => { setEditAg(null); refresh(); }} />}
    </div>
  );
}

function DealSheet({ id, me, onClose, onEdit, onChanged }) {
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(null);
  const load = useCallback(async () => { try { setS(await api.get(`/books/deals/${id}`)); } catch (e) { setError(e.message); } }, [id]);
  useEffect(() => { load(); }, [load]);
  const voidIt = async (p) => {
    const reason = window.prompt('Why void this payment record?');
    if (!reason) return;
    try { await api.post(`/books/payouts/${p.id}/void`, { reason }); load(); onChanged(); } catch (e) { setError(e.message); }
  };
  if (!s) return <Modal onClose={onClose} title="Deal">{error ? <p className="text-rose-600">{error}</p> : 'Loading...'}</Modal>;
  const c = s.calc;
  return (
    <Modal onClose={onClose} title={s.deal.name} maxWidth="max-w-4xl">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-[var(--text-secondary)]">{s.deal.customer} / {s.agreement.title} ({s.agreement.version_key}, {STATUS_LABEL[s.agreement.status]}){s.deal.contract_value_cents ? ` / contract ${usd(s.deal.contract_value_cents)}` : ''}{s.deal.status === 'final' ? ` / final ${fmtDate(s.deal.final_date)}` : ''}</p>
        <div className={`${cardCls} grid grid-cols-2 gap-3 sm:grid-cols-4`}>
          <div><div className="text-xs text-[var(--text-secondary)]">Cash received</div><Money cents={c.receipts_cents} /></div>
          <div><div className="text-xs text-[var(--text-secondary)]">Direct costs so far</div><Money cents={c.costs_cents} /></div>
          <div><div className="text-xs text-[var(--text-secondary)]">Costs deducted</div><Money cents={c.allocated_costs_cents} /><div className="text-[10px] text-[var(--text-secondary)]">{c.cost_method === 'pro_rata' ? `pro rata, ${pct(c.collected_bps)} of the contract collected` : 'all costs'}</div></div>
          <div><div className="text-xs text-[var(--text-secondary)]">Net proceeds</div><Money cents={c.net_proceeds_cents} className="font-semibold" /></div>
        </div>
        {c.net_proceeds_cents < 0 && <p className="text-xs text-amber-700">Net proceeds are below zero, so no fees are earned yet.</p>}
        <table className="w-full text-sm">
          <thead><tr><th className={thCls}>Role</th><th className={thCls}>Member</th><th className={`${thCls} text-right`}>Earned</th><th className={`${thCls} text-right`}>Paid</th><th className={`${thCls} text-right`}>Due</th><th /></tr></thead>
          <tbody>
            {c.roles.map((r) => (
              <tr key={r.role_key} className="border-t border-[var(--border)]">
                <td className={tdCls}>{r.label} <span className="text-xs text-[var(--text-secondary)]">{pct(r.bps)} of {r.of === 'net' ? 'net' : 'Company share'}</span></td>
                <td className={tdCls}>{r.member || <span className="text-[var(--text-secondary)]">Nobody (Company keeps {usd(r.to_company_cents)})</span>}{r.redirected_via ? <span className="text-xs text-[var(--text-secondary)]"> via {r.redirected_via}</span> : ''}</td>
                <td className={`${tdCls} text-right`}><Money cents={r.earned_cents} /></td>
                <td className={`${tdCls} text-right`}><Money cents={r.paid_cents} /></td>
                <td className={`${tdCls} text-right font-medium`}><Money cents={r.due_cents} /></td>
                <td className={`${tdCls} text-right`}>{me.can_edit && r.member && r.due_cents > 0 && <button className={btn2Cls} onClick={() => setPaying(r)}>Mark paid</button>}</td>
              </tr>
            ))}
            <tr className="border-t border-[var(--border)]">
              <td className={tdCls}>Company share</td>
              <td className={`${tdCls} text-xs text-[var(--text-secondary)]`}>{MEMBERS.map((m) => `${m} ${usd(c.company_split_indicative[m])}`).join(', ')} (by ownership, for reference)</td>
              <td className={`${tdCls} text-right`}><Money cents={c.company_share_cents} /></td><td colSpan={3} />
            </tr>
          </tbody>
        </table>
        {c.overpaid && <p className="text-xs text-rose-600">A role has been paid more than it has earned so far (for example after a refund). Review the payments below.</p>}
        {s.payments.length > 0 && (
          <div>
            <h4 className="mb-1 font-semibold text-[var(--text-primary)]">Payments recorded</h4>
            <ul className="space-y-1 text-xs">
              {s.payments.map((p) => (
                <li key={p.id} className={`flex flex-wrap items-center gap-2 ${p.status === 'void' ? 'text-[var(--text-secondary)] line-through' : ''}`}>
                  {fmtDate(p.paid_date)} / {p.member} / {p.role_key} / {usd(p.amount_cents)} / ref {p.reference} / {p.agreement_version}
                  {p.status === 'void' && <span className="no-underline">(void: {p.void_reason})</span>}
                  {me.can_edit && p.status === 'paid' && <button className="text-rose-600" onClick={() => voidIt(p)}><Ban size={12} /></button>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <details>
          <summary className="cursor-pointer text-xs text-[var(--text-secondary)]">Lines behind these numbers ({s.receipt_lines.length} receipts{s.receipts_source.source === 'q2c' ? ' from quote-to-cash' : ''}, {s.cost_lines.length} costs)</summary>
          <ul className="mt-1 text-xs">
            {s.receipt_lines.map((l, i) => <li key={`r${i}`}>{l.date} / received / {l.invoice_number ? `${l.invoice_number} payment` : l.account} / {usd(l.amount_cents)}</li>)}
            {s.cost_lines.map((l, i) => <li key={`c${i}`}>{l.date} / cost / {l.account} / {usd(l.amount_cents)}</li>)}
            {(s.excluded_ledger_receipts || []).map((l, i) => (
              <li key={`x${i}`} className="text-[var(--text-secondary)]">{l.date} / bank deposit, not counted again / {usd(l.amount_cents)} / {l.matched ? `matches ${l.matched.invoice_number} payment (confirm)` : 'no matching payment found: check it'}</li>
            ))}
          </ul>
          <p className="mt-1 text-[10px] text-[var(--text-secondary)]">{s.receipts_source.note}</p>
        </details>
        {error && <p className="text-rose-600">{error}</p>}
        {me.can_edit && <button className={btn2Cls} onClick={() => onEdit(s.deal)}><Pencil size={14} /> Edit deal</button>}
      </div>
      {paying && <MarkPaid deal={s.deal} agreement={s.agreement} role={paying} onClose={() => setPaying(null)} onSaved={() => { setPaying(null); load(); onChanged(); }} />}
    </Modal>
  );
}

function MarkPaid({ deal, agreement, role, onClose, onSaved }) {
  const unsigned = agreement.status !== 'signed';
  const [confirmUnsigned, setConfirmUnsigned] = useState(false);
  const [amount, setAmount] = useState(centsToInput(role.due_cents));
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const cents = parseCents(amount);
  const save = async () => {
    try { await api.post('/books/payouts', { deal_id: deal.id, role_key: role.role_key, amount_cents: cents, paid_date: date, reference, ...(unsigned ? { confirm_unsigned: confirmUnsigned } : {}) }); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal onClose={onClose} title={`Record payment: ${role.label}, ${role.member}`} maxWidth="max-w-md">
      <div className="space-y-2 text-sm">
        <p className="text-xs text-[var(--text-secondary)]">This only records a payment you already made (check, ACH or wire). Nothing is sent. Due now: {usd(role.due_cents)}.</p>
        <label className="block">Amount <input className={`${inputCls} w-full`} value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" /></label>
        <label className="block">Paid on <input type="date" className={`${inputCls} w-full`} value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="block">Reference (check number, Relay transfer id) <input className={`${inputCls} w-full`} value={reference} onChange={(e) => setReference(e.target.value)} /></label>
        {unsigned && (
          <label className="flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
            <input type="checkbox" checked={confirmUnsigned} onChange={(e) => setConfirmUnsigned(e.target.checked)} />
            {agreement.version_key} is not marked signed ({STATUS_LABEL[agreement.status]}). I confirm the members agreed to pay this fee under it.
          </label>
        )}
        {error && <p className="text-rose-600">{error}</p>}
        <button className={btnCls} disabled={!cents || !reference.trim() || (unsigned && !confirmUnsigned)} onClick={save}>Record payment</button>
      </div>
    </Modal>
  );
}

function DealForm({ deal, agreements, onClose, onSaved }) {
  const def = agreements.find((a) => a.is_default) || agreements[0];
  const [f, setF] = useState({
    name: deal?.name || '', customer: deal?.customer || '', lead_id: deal?.lead_id || '', order_id: deal?.order_id || '', agreement_id: deal?.agreement_id || def?.id || '',
    contract: deal?.contract_value_cents ? centsToInput(deal.contract_value_cents) : '', status: deal?.status || 'open', final_date: deal?.final_date || '',
    assignments: deal?.assignments || {}, notes: deal?.notes || '',
  });
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/books/leads').then((r) => setLeads(r.leads)).catch(() => setLeads([])); }, []);
  useEffect(() => { api.get('/orders?status=all').then((r) => setOrders(r.orders || [])).catch(() => setOrders([])); }, []);
  const ag = agreements.find((a) => a.id === f.agreement_id);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const assignments = Object.fromEntries((ag?.config.roles || []).map((r) => [r.key, f.assignments[r.key] || null]));
    // order_id only when set or changed, so a Books-only install (no q2c column) keeps working.
    const orderPart = (f.order_id || deal?.order_id) ? { order_id: f.order_id || null } : {};
    const body = { name: f.name, customer: f.customer || null, lead_id: f.lead_id || null, ...orderPart, agreement_id: f.agreement_id, contract_value_cents: f.contract ? parseCents(f.contract) : null, assignments, notes: f.notes || null, status: f.status, ...(f.status === 'final' ? { final_date: f.final_date || today() } : {}) };
    if (f.contract && body.contract_value_cents == null) { setError('Contract value is not a valid amount.'); return; }
    try {
      if (deal) await api.patch(`/books/deals/${deal.id}`, { ...body, revision: deal.updated_at });
      else await api.post('/books/deals', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal onClose={onClose} title={deal ? 'Edit deal' : 'New deal'} maxWidth="max-w-lg">
      <div className="space-y-2 text-sm">
        <label className="block">Name <input className={`${inputCls} w-full`} value={f.name} onChange={(e) => set('name')(e.target.value)} /></label>
        <label className="block">Customer <input className={`${inputCls} w-full`} value={f.customer} onChange={(e) => set('customer')(e.target.value)} /></label>
        <label className="block">CRM lead (optional)
          <select className={`${inputCls} w-full`} value={f.lead_id} onChange={(e) => set('lead_id')(e.target.value)}>
            <option value="">None</option>
            {leads.map((l) => <option key={l.id} value={l.id}>{[l.company, l.name].filter(Boolean).join(': ')}</option>)}
          </select>
        </label>
        <label className="block">Sales order (optional: its invoice payments become this deal's receipts)
          <select className={`${inputCls} w-full`} value={f.order_id} onChange={(e) => set('order_id')(e.target.value)}>
            <option value="">None (use bank deposits tagged to the deal)</option>
            {orders.map((o) => <option key={o.id} value={o.id}>{o.number} / {o.customer_name || o.title}</option>)}
          </select>
        </label>
        <label className="block">Agreement version
          <select className={`${inputCls} w-full`} value={f.agreement_id} onChange={(e) => set('agreement_id')(e.target.value)}>
            {agreements.map((a) => <option key={a.id} value={a.id}>{a.version_key} ({STATUS_LABEL[a.status]})</option>)}
          </select>
        </label>
        {ag?.config.roles.map((r) => (
          <label key={r.key} className="block">{r.label} ({pct(r.bps)})
            <select className={`${inputCls} w-full`} value={f.assignments[r.key] || ''} onChange={(e) => set('assignments')({ ...f.assignments, [r.key]: e.target.value || null })}>
              <option value="">Nobody</option>
              {MEMBERS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
        ))}
        <label className="block">Contract value (for pro rata fees on partial payments) <input className={`${inputCls} w-full`} value={f.contract} onChange={(e) => set('contract')(e.target.value)} inputMode="decimal" placeholder="Optional" /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.status === 'final'} onChange={(e) => set('status')(e.target.checked ? 'final' : 'open')} /> Final: all money is in and all costs are known</label>
        {f.status === 'final' && <input type="date" className={inputCls} value={f.final_date || today()} onChange={(e) => set('final_date')(e.target.value)} aria-label="Final date" />}
        <label className="block">Notes <textarea className={`${inputCls} w-full`} rows={2} value={f.notes} onChange={(e) => set('notes')(e.target.value)} /></label>
        {error && <p className="text-rose-600">{error}</p>}
        <button className={btnCls} disabled={!f.name.trim() || !f.agreement_id} onClick={save}>Save deal</button>
      </div>
    </Modal>
  );
}

function AgreementForm({ agreement, onClose, onSaved }) {
  const base = agreement || { version_key: '', title: '', status: 'draft', effective_date: '', is_default: false, notes: '', config: { roles: [{ key: 'sales_lead', label: 'Sales Lead', bps: 3000, of: 'net', if_unassigned: 'company' }, { key: 'delivery_lead', label: 'Delivery Lead', bps: 2000, of: 'net', if_unassigned: 'company' }], ownership_bps: { Chad: 4000, Steve: 4000, Chase: 2000 } } };
  const [f, setF] = useState({ ...base, effective_date: base.effective_date || '', notes: base.notes || '' });
  const [roles, setRoles] = useState(base.config.roles.map((r) => ({ ...r, pctText: String(r.bps / 100) })));
  const [own, setOwn] = useState(Object.fromEntries(MEMBERS.map((m) => [m, String(base.config.ownership_bps[m] / 100)])));
  const [error, setError] = useState('');
  const toBps = (t) => { const c = parseCents(t); return c == null ? NaN : c; }; // "30" -> 3000, "12.5" -> 1250
  const updRole = (i, k, v) => setRoles((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    const config = { roles: roles.map((r) => ({ key: r.key, label: r.label, bps: toBps(r.pctText), of: r.of, if_unassigned: r.if_unassigned })), ownership_bps: Object.fromEntries(MEMBERS.map((m) => [m, toBps(own[m])])) };
    const body = { version_key: f.version_key, title: f.title, status: f.status, effective_date: f.effective_date || null, is_default: !!f.is_default, notes: f.notes || null, config };
    try {
      if (agreement) await api.patch(`/books/agreements/${agreement.id}`, { ...body, revision: agreement.updated_at });
      else await api.post('/books/agreements', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal onClose={onClose} title={agreement ? 'Edit agreement version' : 'New agreement version'} maxWidth="max-w-2xl">
      <div className="space-y-2 text-sm">
        <p className="text-xs text-[var(--text-secondary)]">Percentages are frozen once a payment is recorded under a version. To change terms after that, create a new version and use it for new deals.</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label>Version key <input className={`${inputCls} w-full`} value={f.version_key} onChange={(e) => setF({ ...f, version_key: e.target.value })} placeholder="OA-5.1-2026-10" /></label>
          <label>Title <input className={`${inputCls} w-full`} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
          <label>Status
            <select className={`${inputCls} w-full`} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label>Effective date <input type="date" className={`${inputCls} w-full`} value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></label>
        </div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} /> Default for new deals</label>
        <h4 className="pt-2 font-semibold">Roles</h4>
        {roles.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-1.5">
            <input className={`${inputCls} w-32`} value={r.key} onChange={(e) => updRole(i, 'key', e.target.value)} placeholder="key" aria-label="Role key" />
            <input className={`${inputCls} w-40`} value={r.label} onChange={(e) => updRole(i, 'label', e.target.value)} placeholder="Label" aria-label="Role label" />
            <input className={`${inputCls} w-16 text-right`} value={r.pctText} onChange={(e) => updRole(i, 'pctText', e.target.value)} aria-label="Percent" />%
            <select className={inputCls} value={r.of} onChange={(e) => updRole(i, 'of', e.target.value)} aria-label="Of">
              <option value="net">of net proceeds</option><option value="company">of the Company share</option>
            </select>
            <select className={inputCls} value={r.if_unassigned} onChange={(e) => updRole(i, 'if_unassigned', e.target.value)} aria-label="If nobody holds it">
              <option value="company">if nobody: Company keeps it</option>
              {roles.filter((x, j) => j !== i && x.key).map((x) => <option key={x.key} value={`role:${x.key}`}>if nobody: goes to {x.label}</option>)}
            </select>
            <button className="text-[var(--text-secondary)]" onClick={() => setRoles((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove role">x</button>
          </div>
        ))}
        <button className={btn2Cls} onClick={() => setRoles((rs) => [...rs, { key: '', label: '', pctText: '10', of: 'net', if_unassigned: 'company' }])}><Plus size={14} /> Role</button>
        <h4 className="pt-2 font-semibold">Ownership</h4>
        <div className="flex flex-wrap gap-3">
          {MEMBERS.map((m) => <label key={m} className="flex items-center gap-1">{m} <input className={`${inputCls} w-16 text-right`} value={own[m]} onChange={(e) => setOwn({ ...own, [m]: e.target.value })} />%</label>)}
        </div>
        <label className="block">Notes <textarea className={`${inputCls} w-full`} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
        {error && <p className="text-rose-600">{error}</p>}
        <button className={btnCls} onClick={save}>Save version</button>
      </div>
    </Modal>
  );
}
